// English is the source of truth for the message catalogue.
//
// `Messages` is derived from this object, and the other locales are
// typed as `Messages` — so a key added here that is missing from
// zh.ts or ja.ts is a compile error, not a string that silently
// renders in the wrong language.
//
// Keys read `area.thing`. Keep them sorted; it makes a missing
// translation obvious in review.

export const en = {
  "web.severity": "Severity",
  "web.title": "Web monitoring",
  "web.description": "Real-user visits, JavaScript failures and isolated browser checks",
  "web.chooseApp": "Select a Web App in this project",
  "web.noApps": "Create a Web App with an explicit identifier to start collecting visits",
  "web.performance": "Performance",
  "web.checks": "Checks",
  "web.errors": "Errors",
  "web.visits": "Sampled visits",
  "web.documents": "Document visits",
  "web.errorRate": "Estimated visit error rate",
  "web.rumHint": "Percentiles use sampled visits, separated by device. Document metrics and SPA readiness are distinct. No data is not a pass.",
  "web.samples": "Samples",
  "web.device": "Device",
  "web.mobile": "Mobile",
  "web.desktop": "Desktop",
  "web.all": "All",
  "web.environment": "Environment",
  "web.release": "Release",
  "web.route": "Route template",
  "web.hours": "Window (hours)",
  "web.metric": "Metric",
  "web.assessment": "Assessment",
  "web.insufficient": "Insufficient data",
  "web.attention": "Needs attention",
  "web.good": "Good",
  "web.observed": "Observed",
  "web.coverage": "Missing metric coverage",
  "web.recent": "Recent visits",
  "web.diagnostics": "Diagnostics",
  "web.empty": "No observations in this scope",
  "web.errorHint": "Latest 500 error events; correlated visits include only visits carrying the optional RUM context",
  "web.occurrences": "Occurrences",
  "web.correlated": "Correlated visits",
  "web.lastSeen": "Last seen",
  "web.create": "Create check",
  "web.edit": "Edit check",
  "web.run": "Run now",
  "web.refresh": "Refresh",
  "web.runnerMissing": "The browser runner is not configured. Checks cannot run yet.",
  "web.labHint": "Laboratory results are separate from real-user percentiles. TBT is not INP. Search engine indexing is not measured.",
  "web.name": "Name",
  "web.url": "URL",
  "web.policy": "Page policy",
  "web.public": "Public and indexable",
  "web.private": "Private / noindex",
  "web.ready": "Ready element selector",
  "web.auth": "Runner auth profile name",
  "web.authHint": "Credentials stay on the runner and are scoped to this project and app",
  "web.origins": "Additional public origins (one per line)",
  "web.schedule": "Enable recurring checks",
  "web.interval": "Interval (seconds, minimum 900)",
  "web.lighthouse": "Run Lighthouse performance audit",
  "web.alerts": "Notify on persistent failure and recovery",
  "web.threshold": "Consecutive failed runs before notification",
  "web.advanced": "Journey steps (JSON, optional)",
  "web.stepsHint": "Allowed actions: click, waitFor, assertText. Use a dedicated account and read-only journeys.",
  "web.save": "Save",
  "web.cancel": "Cancel",
  "web.delete": "Delete check and history",
  "web.confirmDelete": "Confirm deletion",
  "web.failed": "Operation failed",
  "web.findings": "Findings",
  "web.rule": "Rule",
  "web.evidence": "Evidence",
  "web.advice": "Suggested repair",
  "web.profile": "Profile and tool versions",
  "web.queued": "Queued",
  "web.running": "Running",
  "web.passed": "Passed",
  "web.siteFailed": "Check failed",
  "web.monitorError": "Monitor error",
  "web.cancelled": "Cancelled",
  "web.unknown": "Not checked",
  "web.degraded": "Failure observed",
  "web.failing": "Persistent failure",
  "web.noFindings": "No findings in the checked scope",
  "web.retention": "Retention: {days} days",
  "web.sampleRates": "Sampling probabilities",
  "web.history": "Recent runs",
  "web.budgetLcp": "LCP budget (ms)",
  "web.budgetTbt": "TBT budget (ms)",
  "web.budgetCls": "CLS budget",

  "releaseView.noFiles": "No debug files uploaded",
  "releaseView.uploadedUnchecked": "File stored; format has not been verified.",
  "releaseView.cliHint": "Use sentori to configure, collect source files, upload all debug files, and verify the stored bytes. Git is optional; --git-tag selects an exact tag.",
  "releaseView.sourceZipHint": "Upload a ZIP of this build’s UTF-8 source files, preserving project-relative paths. Limit: 64 MiB expanded, 512 KiB per file. Legacy source JSON is also supported.",
  "releaseView.validationHint": "Uploaded debug files are listed by type. Format validation does not confirm a match to the build or event. Native builds do not need JavaScript source maps unless they also run JavaScript.",
  "releaseView.fileStatus": "{kind}: {status} · {count} files",
  "releaseView.notUploaded": "Not uploaded",
  "artifact.sourceMap": "Source Map",
  "artifact.dsym": "dSYM",
  "artifact.proguard": "ProGuard mapping",
  "artifact.sourceBundle": "Source bundle",
  "bench.noDocuments": "No open tabs",
  "bench.openDocument": "Open a resource or a page from the navigation to start working",
  "bench.reopenDocument": "Reopen last page",
  "releaseView.selected": "Selected release",
  "releaseView.backToList": "Back to list",
  "releaseView.outsideList": "Selected release is outside the current results",
  "releaseView.sections": "Release detail sections",
  "shell.saasBy": "SaaS provided by {name}",
  "shell.saas": "Sentori Cloud · SaaS",
  "shell.selfHostedBy": "Self-hosted by {operator} · {name}",
  "shell.selfHostedInstance": "Self-hosted · {name}",
  "shell.selfHosted": "Self-hosted instance",
  "shell.instanceUnknown": "Instance information unavailable",
  "shell.instanceDetails": "Instance and versions",
  "shell.interfaceVersion": "Console {version}",
  "shell.serviceVersion": "Data service {version}",
  "shell.interface": "Management console version",
  "shell.service": "Data service version",
  "shell.deploymentMode": "Deployment",
  "shell.operator": "Service operator",
  "shell.operatorUnknown": "Not configured by the instance operator",
  "shell.productCredit": "Sentori is developed by GOLIA. The service operator runs this instance.",

  "bench.projectSwitcher": "Workspace project",
  "filters.advanced": "Advanced filters",
  "bench.title": "Workbench",
  "bench.explorer": "Resources",
  "bench.inspector": "Inspector",
  "bench.activity": "Activity",
  "bench.toggleExplorer": "Toggle resources",
  "bench.toggleInspector": "Toggle inspector",
  "bench.toggleActivity": "Toggle bottom panel",
  "bench.reset": "Reset workspace layout",
  "bench.resize": "Resize {name}",
  "bench.tabs": "Investigation tabs",
  "bench.tabHint": "Arrow keys switch tabs; Alt + arrow keys reorder",
  "bench.closeTab": "Close {name}",
  "bench.moved": "{name} moved to {position} of {total}",
  "bench.recent": "Latest {count} of {total}",
  "bench.empty": "No records",
  "bench.loadFailed": "Could not load resources",
  "bench.selectIssue": "Select an issue to view its activity",
  "bench.context": "Current context",
  "bench.project": "Project",
  "bench.status": "Status",
  "bench.release": "Release",
  "bench.environment": "Environment",
  "bench.events": "Events",
  "bench.platform": "Platform",
  "bench.issues": "Issues",
  "bench.releases": "Releases",
  "inspect.event": "Current event",
  "inspect.frame": "Selected frame",
  "inspect.frameNumber": "Stack frame {n}",
  "inspect.function": "Function",
  "inspect.location": "Source location",
  "inspect.sourceAvailable": "Source context is available when this frame is expanded",
  "inspect.selectFrame": "Select a stack frame to inspect its full location and source status",
  "inspect.eventContext": "Event context",
  "inspect.backToEvent": "Back to event",
  "inspect.identifiers": "Identifiers and addresses",
  "inspect.imageBase": "Image base address",
  "bench.selection": "Open a resource to inspect its context",
  "bench.unknown": "Not provided",

  "queue.shortcutButton": "Shortcuts",
  "queue.shortcutMove": "Move between issues",
  "queue.shortcutOpen": "Open issue",
  "queue.shortcutSelect": "Select issue",
  "crashFree.emptyHint": "Statistics appear when clients report sessions",
  "apps.unidentified": "Unidentified app · {platform}",
  "apps.unidentifiedData": "Unidentified data",
  "apps.unidentifiedHint": "These events have no confirmed app identity and are excluded from the app count",
  "apps.events24h": "{count} events in 24 hours",
  "apps.inferredName": "Inferred from a release name — not yet confirmed",
  "apps.nameSource": "Name source",
  "apps.identifier": "App identifier",
  "apps.sourceConfigured": "Set by the project owner",
  "apps.sourceReported": "App identifier reported by the client",
  "apps.sourceRelease": "Inferred from the release name",
  "apps.sourceUnknown": "App identity not available",
  'apps.title': "Apps",
  'apps.all': "All apps",
  'apps.loadFailed': "Could not load apps",
  'apps.platforms': "Detected platforms and integration guides",
  'apps.filesOnly': "Files uploaded",
  'apps.empty': "Apps appear automatically when data arrives. Choose a platform above to connect.",
  'apps.edit': "Edit app",
  'apps.create': 'Add app',
  'apps.identifierHint': 'Use the exact identifier reported by the SDK (bundle ID, package name, or configured app identifier).',
  'apps.kind': 'Application type',
  'apps.name': "App name",
  'apps.repository': "Git repository",
  'apps.repositoryHint': "Repository URL for this app",
  'apps.conflict': "This app changed. Close this dialog, refresh and try again.",

  "instanceHealth.title": "Instance health",
  "instanceHealth.hint": "Read-only checks do not write test data or contact external services. A backup record does not prove an off-host copy or a successful restore drill.",
  "instanceHealth.run": "Check instance health",
  "instanceHealth.unknownCheck": "Other check",
  "instanceHealth.unknownReason": "This result is not recognized. Check the server and dashboard versions.",
  "instanceHealth.status.pass": "Healthy",
  "instanceHealth.status.warning": "Needs attention",
  "instanceHealth.status.fail": "Failed",
  "instanceHealth.status.unknown": "Unverified",
  "instanceHealth.check.database": "Database connectivity",
  "instanceHealth.check.schema": "Database schema",
  "instanceHealth.check.instance": "Instance identity and state",
  "instanceHealth.check.backup": "Recent backup record",
  "instanceHealth.check.operations": "Instance operations",
  "instanceHealth.check.storage": "Persistent storage directory",
  "instanceHealth.check.recovery": "Disaster recovery evidence",
  "instanceHealth.reason.database_reachable": "The database answered the query.",
  "instanceHealth.reason.database_unavailable": "The database did not respond. Check connectivity, service status and credentials.",
  "instanceHealth.reason.metadata_unavailable": "Diagnostic metadata is unavailable. Check the database version and permissions.",
  "instanceHealth.reason.schema_matches_binary": "Migration versions and checksums match this binary.",
  "instanceHealth.reason.schema_mismatch": "Migrations differ from this binary. Check the upgrade or rollback version.",
  "instanceHealth.reason.instance_ready": "The instance is initialized and in normal operating mode.",
  "instanceHealth.reason.recovery_validation_active": "Recovery validation is active. Verify restored data before resuming workers.",
  "instanceHealth.reason.setup_incomplete": "Complete instance setup.",
  "instanceHealth.reason.instance_identity_invalid": "Instance identity or operating mode violates the single-workspace contract.",
  "instanceHealth.reason.recent_backup_record": "A backup completed within 24 hours. This check did not read the archive.",
  "instanceHealth.reason.backup_record_stale": "The latest backup is older than 24 hours. Check the backup schedule.",
  "instanceHealth.reason.backup_clock_invalid": "The backup timestamp is invalid. Check the server clock.",
  "instanceHealth.reason.backup_record_missing": "No completed backup exists for this instance generation. Create and retain an independent backup.",
  "instanceHealth.reason.lifecycle_operation_stalled": "An operation is waiting too long or has an expired lease. Inspect its history and service logs.",
  "instanceHealth.reason.no_stalled_lifecycle_operations": "No overdue queued or expired-lease instance operation was found.",
  "instanceHealth.reason.storage_not_persistent": "Persistent file storage is not configured. Attachments may be lost on restart.",
  "instanceHealth.reason.storage_directory_readable": "The storage directory is readable. Writes, free space and complete file integrity were not tested.",
  "instanceHealth.reason.storage_directory_unreadable": "The storage directory cannot be read. Check its mount and permissions.",
  "instanceHealth.reason.recovery_not_verified": "Off-host copies, recovery keys and an actual restore were not verified. Verify the archive independently and run a restore drill in an isolated instance.",

  "cloud.renameWorkspace": "Rename workspace",
  "cloud.renameOrganization": "Rename organization",
  "cloud.orgOwner": "Organization owner",
  "cloud.orgMember": "Organization member",
  "cloud.makeOwner": "Make owner",
  "cloud.makeMember": "Make member",
  "cloud.removeMember": "Remove member",
  "cloud.memberChangeHint": "Change access for {email}. Organization owners can administer every workspace and its billing. Removing a member immediately revokes their workspace access.",
  "cloud.provisioningFailed": "Workspace provisioning failed. Retry to continue with the existing resources.",
  "cloud.provisioningWait": "Payment is confirmed. Wait for workspace provisioning and check its actual status here.",
  "cloud.quote": "Plan quote",
  "cloud.plan": "Plan",
  "cloud.month": "month",
  "cloud.year": "year",
  "cloud.requestQuote": "Request quote",
  "cloud.billing": "Subscription and billing",
  "cloud.checkoutUnavailable": "Purchases are not available yet.",
  "cloud.internalWorkspace": "Internal workspace operated by the service provider",
  "cloud.trialWorkspace": "Trial workspace",
  "cloud.testBilling": "This is a test payment environment. It does not indicate that the live service has launched.",
  "cloud.paymentStatus": "Payment status",
  "cloud.paidUntil": "Paid service ends at {time}",
  "cloud.continueCheckout": "Continue to checkout",
  "cloud.retryVerification": "Recheck payment",
  "cloud.renewalCancelled": "Automatic renewal is cancelled after the current paid period.",
  "cloud.renewalActive": "Automatic renewal is enabled.",
  "cloud.resumeRenewal": "Resume renewal",
  "cloud.cancelRenewal": "Cancel renewal",
  "cloud.invoices": "Paid invoices",
  "cloud.workspaceMembers": "Workspace access",
  "cloud.workspaceMembersHint": "Organization owners automatically administer the workspace. Other members need explicit access. Project members also need project assignments after their first workspace login.",
  "cloud.revokeAccess": "Revoke access",
  "cloud.selectMember": "Select an organization member",
  "cloud.grantAccess": "Save access",
  "cloud.renewalHint": "The payment service processes the request and confirms its result. Cancelling renewal preserves the current paid period; success appears after verification.",
  "cloud.quoteExpires": "Accept quote by {time}",
  "cloud.serviceTerms": "Service terms",
  "cloud.privacy": "Privacy terms",
  "cloud.refunds": "Refund terms",
  "cloud.agreeTerms": "I have read and accept these terms (revision {revision}).",
  "cloud.acceptQuote": "Accept quote and prepare checkout",
  "cloud.taxIncluded": "tax included",
  "cloud.taxExtra": "tax calculated at checkout",

  "cloud.signIn": "Sign in to Sentori Cloud",
  "cloud.register": "Create a Cloud account",
  "cloud.verify": "Verify your account",
  "cloud.linkSent": "If the address is eligible, a verification email has been queued. Check your inbox.",
  "cloud.emailFirst": "Verify your email before setting a password.",
  "cloud.displayName": "Display name",
  "cloud.mailUnavailable": "Email service is not configured. Try again later.",
  "cloud.actionFailed": "The operation could not be completed. Check the reason and retry.",
  "cloud.sendLink": "Send verification email",
  "cloud.accessInvalid": "This sign-in request is invalid. Start again from the workspace.",
  "cloud.enter": "Enter workspace",
  "cloud.console": "Cloud console",
  "cloud.signOut": "Sign out of Cloud",
  "cloud.organizations": "Organizations",
  "cloud.passwordSessions": "Changing your password signs out all Cloud sessions and their workspace logins. Sign in again afterward.",
  "cloud.invitations": "Pending invitations",
  "cloud.acceptInvitation": "Accept invitation",
  "cloud.previous": "Previous",
  "cloud.next": "Next",
  "cloud.organizationName": "Organization name",
  "cloud.createOrganization": "Create organization",
  "cloud.workspaces": "Workspaces",
  "cloud.manageWorkspace": "Manage workspace",
  "cloud.workspaceName": "Workspace name",
  "cloud.region": "Data region",
  "cloud.createWorkspace": "Create workspace draft",
  "cloud.catalogUnavailable": "The operator has not configured available regions and plans.",
  "cloud.members": "Organization members",
  "cloud.inviteHint": "The invitation appears in the recipient’s Cloud console. Invitation emails are not sent yet.",
  "cloud.invite": "Invite member",
  "cloud.revokeInvitation": "Revoke invitation",
  "cloud.state.draft": "Draft",
  "cloud.state.checkout": "Awaiting checkout",
  "cloud.state.paid": "Paid, awaiting provisioning",
  "cloud.state.provisioning": "Provisioning",
  "cloud.state.ready": "Ready",
  "cloud.state.suspended": "Suspended",
  "cloud.state.closed": "Closed",

  "cloud.workspace": "Cloud workspace",
  "cloud.usage": "Workspace usage",
  "cloud.usageFailed": "Workspace usage could not be loaded.",
  "cloud.events": "Events this month",
  "cloud.replays": "Replays this month",
  "cloud.ingestBytes": "Ingest bytes this month",
  "cloud.artifactBytes": "Stored artifact bytes",
  "cloud.resetAt": "Monthly usage resets at {time} (UTC calendar month).",
  "cloud.unavailable": "Ingestion authorization is currently unavailable.",
  "cloud.retention": "Events: {events} days · Replays: {replays} days · Up to {rate} events/second",
  "cloud.trafficHint": "Traffic counts normalized JSON and uploaded file bytes, including admitted retries. Deleting data does not refund monthly usage.",

  "auth.modeFailed": "The sign-in method could not be loaded.",
  "auth.cloudTitle": "Sentori Cloud",
  "auth.cloudHint": "Sign in with the Cloud account that has access to this workspace.",
  "auth.cloudManaged": "Your password and organization membership are managed in Sentori Cloud.",
  "auth.cloudAccount": "Manage Cloud account",
  "auth.cloudSignIn": "Continue with Cloud",
  "auth.cloudMembers": "Manage membership in Sentori Cloud. Assign projects here after a member first signs in to this workspace.",

  "lifecycle.database": "Database",
  "lifecycle.files": "Replay and source files",
  "lifecycle.upload": "Upload and verify",
  "lifecycle.verify": "Verify archive",
  "lifecycle.uploadedArchive": "Uploaded archive",
  "lifecycle.instanceBackup": "Instance backup",
  "lifecycle.sourceInstance": "Source instance",
  "lifecycle.archiveFile": "Encrypted backup file",
  "lifecycle.keyFile": "Recovery key file",
  "lifecycle.invalidKey": "Choose the 64-character hexadecimal recovery key file saved for this archive.",
  "lifecycle.uploadHint": "Upload an encrypted recovery archive and its saved key for independent verification. This checks the file; it does not restore data. Browser uploads support up to 1 GiB. Use the operator CLI for larger files.",
  "lifecycle.uploadTooLarge": "This archive exceeds 1 GiB. Verify it with the operator CLI.",

  "reset.title": "Reset instance",
  "reset.hint": "Clear instance data after a fresh verified backup, then set up again.",
  "reset.consequences": "Reset removes projects, events, replay files, members, sessions, credentials, and queued work. The instance ID and name stay; the generation increases. The offline tool first creates and verifies a complete encrypted backup. Existing encrypted backup files and deployment settings remain under operator control.",
  "reset.typeName": "Type the instance name",
  "reset.typeId": "Type the instance ID",
  "reset.typeGeneration": "Type the generation",
  "reset.password": "Current owner password",
  "reset.acknowledge": "I understand that reset clears this instance. I will use independent storage for the backup and journal and keep the recovery key separately.",
  "reset.prepare": "Prepare offline reset",
  "reset.wrongPassword": "The current password is incorrect. Try again.",
  "reset.failed": "The reset permit could not be confirmed. Refresh the instance and prepare again; a new permit replaces the previous one.",
  "reset.prepared": "Reset is authorized for offline execution. No data has been cleared.",
  "reset.expires": "Permit expires",
  "reset.downloadPermit": "Download reset permit",
  "reset.storageStep": "Download the permit. Mount independent recovery storage at /recovery and keep a private recovery key at /keys/recovery.key. These example paths must match the operator environment.",
  "reset.stopStep": "Stop every server replica and other operator commands for this database. Keep PostgreSQL running.",
  "reset.executeStep": "Run the command below with the instance database connection and master key. It creates a fresh backup, reads it back for verification, records the journal, and only then clears data.",
  "reset.restartStep": "Restart only after the command succeeds. Configure the owner for the new setup, check the environment, and initialize the instance again.",
  "reset.interrupted": "If cleanup is interrupted after the database is cleared, startup is blocked. Preserve the backup, key and journal, then use the command below to finish cleanup. Do not manually remove the pending state.",
  "lifecycle.title": "Self-hosted",
  "lifecycle.description": "Manage this instance, encrypted backups and recovery.",
  "lifecycle.overview": "Overview",
  "lifecycle.setup": "Configuration",
  "lifecycle.backups": "Backups",
  "lifecycle.activity": "Activity",
  "lifecycle.ownerOnly": "Only an instance owner can create or download backups.",
  "lifecycle.latestBackup": "Latest verified backup",
  "lifecycle.noBackup": "No backup available",
  "lifecycle.generation": "Generation",
  "lifecycle.recovery": "Recovery",
  "lifecycle.offInstance": "Download the archive to separate storage. A backup on this server cannot protect against losing the server. Keep the recovery key and the instance master key separately.",
  "lifecycle.recoveryHint": "Restore into an empty database and a new blob directory with the target server stopped. Start the restored server in validation mode, check replay and source mapping, then explicitly resume after confirming the old instance has stopped.",
  "lifecycle.runbook": "Read the deployment and recovery guide",
  "lifecycle.create": "Create backup",
  "lifecycle.backupHint": "Creates a consistent database snapshot with all referenced replay and source files, encrypts it with your recovery key, and verifies the persisted archive before making it available.",
  "lifecycle.encrypted": "Encrypted recovery package",
  "lifecycle.configureHint": "Configure durable filesystem storage, SENTORI_MASTER_KEY and an absolute SENTORI_BACKUP_DIR on the server to enable backups.",
  "lifecycle.rows": "rows",
  "lifecycle.cancelling": "Cancelling",
  "lifecycle.verifying": "Verifying archive",
  "lifecycle.state.queued": "Queued",
  "lifecycle.state.running": "Capturing",
  "lifecycle.state.ready": "Verified",
  "lifecycle.state.failed": "Failed",
  "lifecycle.state.cancelled": "Cancelled",
  "lifecycle.state.unavailable": "File unavailable",
  "lifecycle.reason.cancelled": "Cancelled before completion.",
  "lifecycle.reason.instance_changed": "The instance changed. Start a new operation for the current instance.",
  "lifecycle.reason.instance_restored": "This history came from a restored instance. Retrieve the archive from its original storage.",
  "lifecycle.reason.key_unavailable": "The saved recovery key could not be opened. Check the instance master key.",
  "lifecycle.reason.capture_failed": "Backup capture failed. Check server storage and logs, then create a new backup.",
  "lifecycle.reason.verification_failed": "Verification failed. Check the recovery key, archive completeness and operator version.",
  "lifecycle.reason.retry_exhausted": "The worker exceeded its retry limit. Check server logs before starting a new operation.",
  "lifecycle.operationFailed": "This operation did not produce a downloadable backup.",
  "lifecycle.download": "Download archive",
  "lifecycle.verifyHint": "Use the matching CLI version. Set DATABASE_URL to the empty target and provision the original SENTORI_MASTER_KEY separately; the archive does not contain that key. Verification needs only the archive and recovery key.",
  "lifecycle.auditHint": "Creation, cancellation, verification completion and download starts are recorded in the instance audit log. Downloading is not proof that an off-server copy was safely stored.",
  "lifecycle.openAudit": "Open audit log",
  "lifecycle.keyHint": "Save this new recovery key before creating the backup. It is required to decrypt the archive and cannot be retrieved from backup history. The server temporarily stores it encrypted while the task runs, then removes it.",
  "lifecycle.saveKey": "Download recovery key",
  "lifecycle.savedKey": "I have saved the recovery key in a secure place.",
  "lifecycle.uncertain": "The result is not confirmed. Retry sends the same command without creating a second backup.",

  "pushOps.stoppedKinds": "Failed, uncertain or cancelled",
  "pushOps.deviceSearch": "Search ID, token tail or app version",
  "pushOps.setupOnly": "Setup checks describe configuration and registered devices. Delivery history confirms actual outcomes.",
  "pushOps.setupReady": "No setup issue detected",

  "pushOps.description": "Send reviewed notifications and inspect real delivery outcomes.",
  "pushOps.accepted24h": "Accepted in 24h",
  "pushOps.liveDevices": "Active devices",
  "pushOps.search": "Search notification title or body",
  "pushOps.provider": "Provider",
  "pushOps.allProviders": "All providers",
  "pushOps.retryVisible": "Retry reviewed failures",
  "pushOps.status": "Status",
  "pushOps.retries": "Retries",
  "pushOps.created": "Created",
  "pushOps.details": "Details",
  "pushOps.noSends": "No notifications match these filters",
  "pushOps.confirmSend": "Confirm send",
  "pushOps.review": "Review notification",
  "pushOps.queuedResult": "Notification queued. Check delivery history for its outcome.",
  "pushOps.reviewDevice": "This notification will be sent to the selected device.",
  "pushOps.chooseDevice": "Choose a registered device",
  "pushOps.credential": "Credential",
  "pushOps.accepted": "Provider accepted",
  "pushOps.receipt": "SDK receipt",
  "pushOps.noReceipt": "No SDK receipt recorded",
  "pushOps.attempts": "Recent provider attempts",
  "pushOps.noAttempts": "The worker has not recorded an attempt",
  "pushOps.duplicateAck": "The provider may already have accepted this notification. I understand that retrying can send it twice.",
  "pushOps.confirmRetry": "Confirm retry",
  "pushOps.bulkHint": "Retry these {count} failed notifications. Review the list before continuing.",
  "pushOps.deviceScope": "Device visibility",
  "pushOps.allDevices": "All devices, including revoked",
  "pushOps.revoked": "Revoked",
  "pushOps.live": "Active",
  "pushOps.addressable": "User identified",
  "pushOps.yes": "Yes",
  "pushOps.no": "No",
  "pushOps.lastSeen": "Last seen",
  "pushOps.confirmRevoke": "Confirm revocation",
  "pushOps.revoke": "Revoke device",
  "pushOps.badStreak": "Consecutive failures",
  "pushOps.revokeHint": "This registration will stop receiving notifications. Queued sends will be cancelled before dispatch.",
  "pushOps.stagedHint": "New credentials are staged first. Verify and activate them when you are ready to switch.",
  "pushOps.vaultMissing": "Configure the instance encryption key before adding provider credentials.",
  "pushOps.active": "Active",
  "pushOps.staged": "Staged",
  "pushOps.verification": "Last verification",
  "pushOps.unverified": "Not verified",
  "pushOps.fileTooLarge": "Choose a credential file of 64 KiB or less",
  "pushOps.saveStaged": "Save staged credential",
  "pushOps.uploadCredential": "Credential file",
  "pushOps.probe": "Verify with provider",
  "pushOps.activate": "Activate credential",
  "pushOps.encrypted": "Encrypted at rest",
  "pushOps.legacy": "Legacy credential awaiting encryption",
  "pushOps.forceAck": "Verification has not confirmed this credential. I understand that activating it can interrupt delivery.",
  "pushOps.deleteHint": "Deleting this credential can prevent queued notifications from being sent. Confirm the credential above.",
  "pushOps.confirmDelete": "Confirm deletion",
  "pushOps.join": "Match conditions",
  "pushOps.source": "Condition source",
  "pushOps.key": "Field name",
  "pushOps.operator": "Comparison",
  "pushOps.value": "Value",
  "pushOps.addCondition": "Add condition",
  "pushOps.preview": "Preview audience",
  "pushOps.matched": "matching devices",
  "pushOps.previewExpires": "Preview expires at",
  "pushOps.traits": "Traits",
  "pushOps.state.queued": "Queued",
  "pushOps.state.sending": "Sending",
  "pushOps.state.sent": "Provider accepted",
  "pushOps.state.acknowledged": "SDK acknowledged",
  "pushOps.state.failed": "Failed",
  "pushOps.state.uncertain": "Outcome uncertain",
  "pushOps.state.cancelled": "Cancelled",
  "pushOps.verdict.ok": "Verified",
  "pushOps.verdict.rejected": "Rejected",
  "pushOps.verdict.malformed": "Invalid format",
  "pushOps.verdict.unreachable": "Provider unreachable",
  "pushOps.verdict.limited": "Limited permission",
  "pushOps.verdict.not_implemented": "Verification unavailable",

  "monitor.finalStatusHint": "Final status after redirects: 200–299 or 400–599.",
  "integration.title": "Integrations",
  "integration.description": "Configure delivery channels and inspect actual outcomes.",
  "integration.configuredHint": "Configured means settings are saved. Delivery results show whether a request reached its destination.",
  "integration.email": "Email",
  "integration.unconfigured": "Not configured",
  "integration.configured": "Configured",
  "integration.hint.email": "Send through this instance’s configured SMTP service.",
  "integration.hint.webhook": "Signed JSON notifications with stable delivery IDs.",
  "integration.hint.github": "Create issues in a repository using a token with issue write access.",
  "integration.hint.jira": "Create issues through the Jira Cloud v3 API.",
  "integration.destination": "Destination",
  "integration.smtpMissing": "SMTP is not configured on this instance.",
  "integration.configure": "Configure",
  "integration.sendTest": "Send test",
  "integration.confirmSend": "Send now",
  "integration.exportTask": "Copy to external tracker",
  "integration.exportHint": "Create an external issue with this task’s title and description. Sentori’s task status is unchanged.",
  "integration.createsTicket": "This creates a real issue in the configured external project.",
  "integration.realSend": "This sends a real notification to the configured destination.",
  "integration.queueHint": "The request enters a durable queue. Check the delivery log for the provider’s response.",
  "integration.queuedHint": "Queued. Open the delivery log and refresh to see the result.",
  "integration.token": "Credential / signing secret",
  "integration.recipient": "Recipient email",
  "integration.jiraEndpoint": "Jira site URL",
  "integration.accountEmail": "Jira account email",
  "integration.projectKey": "Jira project key",
  "integration.issueType": "Jira issue type ID",
  "integration.secretHint": "Stored encrypted. New HTTP integrations require HTTPS.",
  "integration.keepSecret": "Leave blank to keep the stored value. Changing the endpoint requires a new credential.",
  "integration.events": "Automatic notifications",
  "integration.autoSend": "Selected events send notifications automatically. No events are selected by default.",
  "integration.autoTickets": "Each selected event creates a new external issue. Leave all unchecked for manual copies only.",
  "integration.event.issueNew": "New issue",
  "integration.event.issueRegressed": "Issue regression",
  "integration.event.taskAssigned": "Task assignment",
  "integration.event.taskUpdated": "Task update",
  "integration.deliveries": "Delivery log",
  "integration.noDeliveries": "No deliveries yet.",
  "integration.state.queued": "Queued",
  "integration.state.running": "Sending",
  "integration.state.retryWait": "Waiting to retry",
  "integration.state.delivered": "Delivered",
  "integration.state.failed": "Failed",
  "integration.state.uncertain": "Outcome uncertain",
  "integration.state.cancelled": "Cancelled",
  "integration.attempt": "Attempts: {count} / 5",
  "integration.openExternal": "Open external issue",
  "integration.retry": "Retry delivery",
  "integration.uncertainHint": "The destination may have accepted this request before the connection or worker stopped. Check the destination before retrying.",
  "integration.retryHint": "Retry with the same delivery ID. Current configuration and access are checked again.",
  "integration.duplicateAck": "I checked the destination and accept that retrying may create a duplicate.",

  "monitor.title": "Service status",
  "monitor.description": "Scheduled checks, incident history and recovery notifications.",
  "monitor.publicOnly": "Checks accept public HTTP/HTTPS destinations on ports 80 and 443. Private networks and URL credentials are blocked.",
  "monitor.create": "New monitor",
  "monitor.edit": "Edit monitor",
  "monitor.empty": "No monitors in this project.",
  "monitor.unknown": "No measurement",
  "monitor.operational": "Operational",
  "monitor.degraded": "Check failed",
  "monitor.down": "Incident open",
  "monitor.paused": "Paused",
  "monitor.availability": "Availability · 30 days",
  "monitor.latency": "Response time",
  "monitor.lastCheck": "Last check",
  "monitor.samples": "Recent checks",
  "monitor.window": "{count} checks in the last 30 days with this configuration.",
  "monitor.incident": "Consecutive failures reached the configured threshold. A successful check will close this incident.",
  "monitor.failureThreshold": "Failure threshold",
  "monitor.checkNow": "Check now",
  "monitor.noHistory": "No records yet.",
  "monitor.incidents": "Incident history",
  "monitor.recovered": "Recovered",
  "monitor.name": "Monitor name",
  "monitor.url": "Health check URL",
  "monitor.intervalSeconds": "Interval (minutes)",
  "monitor.timeoutSeconds": "Timeout (seconds)",
  "monitor.expectedStatus": "Expected HTTP status",
  "monitor.editHint": "Saving starts a new measurement period and cancels any pending result from the previous configuration. Incident history is retained.",
  "monitor.deleteHint": "Delete this monitor and stop future checks? Existing incident records remain in storage.",
  "monitor.confirmDelete": "Delete monitor",

  "team.autoWork": "started work",
  "team.autoPrOpened": "opened a pull request",
  "team.autoPrMerged": "merged a pull request",
  "team.autoChecks": "reported CI results",
  "team.autoDeployment": "reported a deployment",
  "team.commit": "Commit",
  "team.pullRequest": "Pull request",
  "team.unitChecks": "Unit tests",
  "team.e2eChecks": "End-to-end tests",
  "team.deployedRelease": "Deployed release",
  "team.passed": "Passed",
  "team.checkFailed": "Failed",

  "team.markAllRead": "Mark all read",
  "team.formerAssignee": "Former project member",
  "team.issueTask": "View linked task",

  "team.automation": "Task automation",
  "team.integrationHint": "Connect your CI and deployment system with signed events. Repository, commit and environment must match before a task can be verified.",
  "team.vaultRequired": "Set SENTORI_MASTER_KEY on the server to enable encrypted integration credentials.",
  "team.repository": "Repository",
  "team.environment": "Deployment environment",
  "team.enabled": "Enabled",
  "team.rotate": "Replace the signing key; the old key will stop working",
  "team.signingKey": "Signing key",
  "team.secretOnce": "Shown only now. Save this key in your CI secret store.",
  "team.endpoint": "Webhook endpoint",
  "team.signatureHelp": "Send X-Sentori-Timestamp (Unix seconds) and X-Sentori-Signature: sha256=<HMAC-SHA256 of timestamp + \".\" + raw JSON, using the signing key>. The timestamp must be within five minutes.",

  "team.save": "Save",
  "team.cancel": "Cancel",
  "team.search": "Search tasks",

  "team.title": "Tasks",
  "team.description": "Move work from triage through review and verification.",
  "team.create": "New task",
  "team.edit": "Edit task",
  "team.taskTitle": "Title",
  "team.details": "Description",
  "team.kind": "Kind",
  "team.task": "Task",
  "team.bug": "Bug",
  "team.priority": "Priority",
  "team.urgent": "Urgent",
  "team.high": "High",
  "team.normal": "Normal",
  "team.low": "Low",
  "team.assignee": "Assignee",
  "team.unassigned": "Unassigned",
  "team.everyone": "Everyone",
  "team.mine": "Assigned to me",
  "team.stage": "Stage",
  "team.triage": "Triage",
  "team.ready": "Ready",
  "team.inProgress": "In progress",
  "team.review": "Review",
  "team.verify": "Verification",
  "team.done": "Done",
  "team.cancelled": "Cancelled",
  "team.due": "Due date",
  "team.reproduction": "Reproduction",
  "team.rootCause": "Root cause",
  "team.view": "View",
  "team.list": "List",
  "team.board": "Board",
  "team.all": "All",
  "team.refresh": "Refresh",
  "team.pageHint": "{count} tasks · This page shows up to 50 tasks in either view.",
  "team.empty": "No matching tasks",
  "team.failed": "Could not update collaboration data",
  "team.chooseProject": "Choose an accessible project to view tasks.",
  "team.uncertain": "The response was lost. Retry this saved command to confirm its result.",
  "team.conflict": "Your draft is preserved. If the task changed, close this editor, refresh the task, then edit the current version.",
  "team.issue": "Linked issue",
  "team.manual": "Change stage manually",
  "team.manualHint": "Manual completion resolves a linked issue and records your action. It does not certify CI or deployment verification.",
  "team.evidence": "Verification evidence",
  "team.verified": "Matching commit verified and deployed",
  "team.noEvidence": "No complete verified build and deployment evidence yet.",
  "team.activity": "Activity",
  "team.system": "System",
  "team.created": "created the task",
  "team.updated": "updated the task",
  "team.stageChanged": "changed the stage",
  "team.commented": "commented",
  "team.regressed": "reopened after issue regression",
  "team.comment": "Comment",
  "team.mention": "Mentions",
  "team.noMention": "No mention",
  "team.post": "Post comment",
  "team.inbox": "Personal inbox",
  "team.inboxDescription": "Assignments, mentions and updates for projects you can access.",
  "team.preferences": "Notification preferences",
  "team.unread": "Unread",
  "team.archived": "Archived",
  "team.inboxCount": "{count} unread notifications",
  "team.inboxEmpty": "No notifications in this view",
  "team.assignment": "Assignments",
  "team.taskUpdates": "Task updates",
  "team.monitors": "Monitor notifications",
  "team.markRead": "Mark read",
  "team.markUnread": "Mark unread",
  "team.archive": "Archive",
  "team.restore": "Restore to inbox",

  "jobs.title": "Reprocess stored events",
  "jobs.hint": "Recheck the newest 20,000 error events stored before this job, using the debug files currently available. Issue grouping stays unchanged.",
  "jobs.run": "Start reprocessing",
  "jobs.refresh": "Refresh jobs",
  "jobs.queued": "Queued",
  "jobs.running": "Running",
  "jobs.retryWait": "Waiting to retry",
  "jobs.succeeded": "Completed",
  "jobs.failed": "Failed",
  "jobs.cancelled": "Cancelled",
  "jobs.actionFailed": "Could not update background jobs",
  "jobs.empty": "No reprocessing jobs yet",
  "jobs.progress": "Scanned {scanned} · Updated {rewritten} · Frames recovered {frames}",
  "jobs.attempt": "Attempt {attempt} of {max}",
  "jobs.limit": "Reached the 20,000-event limit for this pass",
  "jobs.cancel": "Cancel job",
  "jobs.cancelling": "Cancellation requested",
  "jobs.processingFailed": "Processing failed; check the server logs for details",
  "jobs.timeout": "A processing batch timed out",
  "jobs.exhausted": "The retry limit was reached",
  "jobs.generationChanged": "The instance changed; start a new job",
  "detail.firstSeen": "First seen",
  "detail.previousAssignee": "Previous assignee · {id}",
  "detail.rawEvent": "Raw event",
  'releaseView.notFound': 'This release is unavailable in this project',
  "releaseView.description": "Check release health and the files used to recover readable stacks.",
  "releaseView.search": "Search releases",
  "releaseView.sessions": "Sessions",
  "releaseView.artifacts": "Debug files",
  "releaseView.created": "Created",
  "releaseView.healthFailed": "Could not load release health",
  "releaseView.artifactFailed": "Could not load debug files",
  "releaseView.window": "Last 30 days",
  "releaseView.usable": "Format checked",
  "releaseView.unusable": "Invalid format",
  "releaseView.unchecked": "Not verified",
  "releaseView.upload": "Upload debug file",
  "releaseView.file": "File",
  "releaseView.fileHint": "Gzip is supported. The upload must be smaller than 256 MiB. Use the matching build file.",
  "releaseView.actionFailed": "Upload failed; the selected file is available to retry",
  "releaseView.uploaded": "File stored and format checked. Existing events will be reprocessed; build matching is checked per event.",
  "releaseView.uploadedInvalid": "File stored, but it cannot be read for symbolication. Check the file type and build.",
  "releaseView.deleteHint": "This removes the release and all its debug files. Existing events are retained.",
  "replay.stepBack": "Previous frame",
  "replay.stepForward": "Next frame",
  "replay.speed": "Playback speed",
  "detail.assignee": "Assignee",
  "detail.unassigned": "Unassigned",
  "detail.lastReceived": "Last received",
  "detail.evidence": "Evidence",
  "detail.activity": "Activity",
  "detail.event": "Occurrence",
  "detail.selectEvent": "Select an event",
  "detail.eventsFailed": "Could not load occurrences",
  "detail.eventFailed": "Could not load this event",
  "detail.foreignEvent": "This event does not belong to this issue",
  "detail.contextFailed": "Could not load nearby events",
  "detail.noEvents": "No retained events for this issue",
  "detail.addNote": "Add a note",
  "detail.note": "Note",
  "detail.saveNote": "Save note",
  "detail.activityLimit": "Latest 100 activity entries",
  "detail.system": "System",
  "detail.noActivity": "No recorded activity",
  "detail.activity.note": "Note",
  "detail.activity.assign": "Assignment",
  "detail.activity.status": "Status change",
  "detail.activity.regression": "Regression",
  "queue.title": "Issues",
  "queue.description": "Search and triage issues using recorded events.",
  "queue.partialFailure": "{failed} of {total} changes failed. Successful changes were saved.",
  "queue.events": "Events",
  "queue.filteredTotals": "Across all matching issues",
  "queue.latestReleases": "Latest releases",
  "queue.all": "All",
  "queue.search": "Search issues",
  "queue.status": "Status",
  "queue.kind": "Type",
  "queue.sort": "Sort",
  "queue.impact": "Impact",
  "queue.newest": "Most recent event",
  "queue.optionsFailed": "Could not load filter options",
  "queue.actionFailed": "Some issues could not be updated",
  "queue.selectPage": "Select this page",
  "queue.shortcuts": "Keyboard shortcuts",
  "queue.noMatches": "No matching issues",
  "queue.select": "Select",
  "queue.selectIssue": "Select {title}",
  "queue.issue": "Issue",
  "queue.users": "Users",
  "queue.usersUnknown": "No identified users recorded",
  "queue.lastSeen": "Last event · device clock",
  "queue.pageCount": "{first}–{last} of {total}",
  "queue.previous": "Previous",
  "queue.next": "Next",
  "queue.noMatchesHint": "Clear the filters to see other issues",
  "setup.stepPending": "Not started",
  "setup.stepCurrent": "Current step",
  "setup.stepComplete": "Complete",
  "setup.stepError": "Needs attention",
  "setup.stepBlocked": "Unavailable",
  "setup.stepSkipped": "Skipped",
  "setup.optional": "Optional",
  "setup.back": "Back",
  "setup.next": "Next",
  "setup.installStepHint": "Install the SDK package for your platform",
  "setup.configureStepHint": "Configure the endpoint and ingest key before running the test",
  "settings.allProjects": "All projects",
  "settings.manageAccess": "Manage access",
  "settings.memberActionFailed": "Could not complete the member action.",
  "settings.description": "Manage project access, credentials and your account",
  "settings.selectProject": "Select an accessible project to manage its tokens",
  "settings.tokenRotation": "To rotate a key, generate a replacement, update your clients, then revoke the old key.",
  "settings.tokensLoadFailed": "Could not load tokens.",
  "settings.tokenActionFailed": "Could not complete the token action.",
  "settings.tokenScopeHint": "ingest keys send SDK events; api keys give automation access to this project.",
  'settings.passwordSessionHint': 'Saving signs out other devices while keeping this session active.',
  'settings.passwordFailed': 'Could not change the password. Check your current password and try again.',
  'projects.conflict': 'This project changed. Reload it before saving again.',
  'projects.reload': 'Reload project',
  'projects.description': 'Review connections, issues and releases across your projects',
  'projects.search': 'Search projects',
  'projects.allPlatforms': 'All platforms',
  'projects.edit': 'Edit project',
  'projects.open': 'Open issues',
  'projects.healthUnavailable': 'Health data unavailable',
  'projects.connected': 'Connected',
  'projects.loading': 'Loading status',
  'projects.waiting': 'Waiting for the first event',
  'projects.errors24h': 'Errors in 24 hours',
  'projects.warnings24h': 'Warnings in 24 hours',
  'projects.users24h': 'Active users in 24 hours',
  'projects.latestRelease': 'Latest release',
  'projects.lastEvent': 'Last event',
  'projects.noMatches': 'No matching projects',
  'projects.filterHint': 'Change your search or clear the filters',
  'projects.clearFilters': 'Clear filters',
  'projects.actionFailed': 'Could not complete the project action',
  'projects.confirmName': 'Type the project name to confirm deletion',
  'auth.sendFailed': 'Could not send the reset request. Try again.',
  "settings.tab.instance": "Instance",
  "instance.recoveryValidation": "Recovery validation",
  "instance.recoveryHint": "Automatic sending, monitoring, background jobs and retention are paused. Verify the recovered data, stop the old instance, then explicitly resume this instance with the operator CLI.",
  "instance.identity": "Instance identity",
  "instance.name": "Instance name",
  "instance.id": "Instance ID",
  "instance.origin": "Public URL",
  "instance.version": "Server version",
  "instance.status": "Setup status",
  "instance.ready": "Initialized",
  "instance.pending": "Setup incomplete",
  "instance.environment": "The public URL and owner account are managed through the deployment environment",
  "instance.checks": "Environment checks",
  "instance.checkHint": "Checks verify database connectivity, a file write/read/delete round trip and public URL configuration. They do not verify external reachability or backup recovery.",
  "instance.check.database": "Database connectivity",
  "instance.check.blobStorage": "File write, read and delete",
  "instance.check.durableStorage": "Filesystem storage configured",
  "instance.check.originValid": "Public URL configuration",
  "instance.unchecked": "Not checked",
  "instance.passed": "Passed",
  "instance.failed": "Failed",
  "instance.lastCheck": "Last checked",
  "instance.runChecks": "Run checks",
  "instance.initialize": "Finish setup",
  "instance.refresh": "Reload instance",
  "instance.loadFailed": "Could not load the instance",
  "instance.actionFailed": "The operation could not be completed",
  "instance.conflict": "The configuration changed. Reload the instance before trying again.",
  "instance.checkRequired": "Run the environment checks again before finishing setup",
  "instance.checked": "Checks completed",
  'common.close': 'Close',
  'impact.events': '{count} events',
  'impact.maximum': 'Up to {max} events per identified user',
  'impact.users': '{count} users',
  'nav.setup': 'Connect an app',
  'setup.alerts': 'Configure notifications',
  'setup.appStartup': 'App startup',
  'setup.askOwner': 'Ask the instance owner to create a project and give you access.',
  'setup.check': 'Check for my test event',
  'setup.checking': 'Checking…',
  'setup.chooseProject': 'Choose a project',
  'setup.configureTitle': 'Connect this project',
  'setup.copied': 'Copied',
  'setup.copy': 'Copy',
  'setup.copyCode': 'Copy this code',
  'setup.copyFailed': 'Clipboard unavailable. Select and copy the code manually.',
  'setup.createKey': 'Create an ingest key',
  'setup.createTitle': 'Create your first connection',
  'setup.creating': 'Creating…',
  'setup.developmentHint': 'This example uses the development environment. Use your actual release string in production and in symbol uploads. Visual replay stays off unless you enable it.',
  'setup.endpoint': 'Your instance URL',
  'setup.failed': 'Could not complete this step',
  'setup.ingestKey': 'App ingest key',
  'setup.install.android': 'Use the dependency version in the Kotlin guide. Register your Application subclass in AndroidManifest.xml; without it the SDK never starts.',
  'setup.install.ios': 'Add the Swift package in Xcode and select the Sentori product. Put startup code in your App initialiser or app delegate. Import Foundation for the test snippet.',
  'setup.install.rn': 'For Expo, use a development build. For bare React Native, install the iOS pods after adding the package. The guide covers both paths.',
  'setup.install.weapp': 'Build npm in WeChat DevTools and initialise in onLaunch. Add your instance domain to the request allowlist before testing.',
  'setup.install.web': 'Works with your existing framework. Initialise once in your browser entry point, before the code you want to monitor.',
  'setup.installTitle': 'Install for your platform',
  'setup.keyHint': 'Use an ingest key in your app, never an automation key. A generated key is shown once and is not saved in this browser. Revoke unused keys in Settings.',
  'setup.localEndpoint': 'This address points to the device running the app. A real phone needs an instance URL it can reach; localhost on your computer is not that address.',
  'setup.nextHint': 'Upload the matching source maps or native symbols, then test an alert. Check data masking before enabling replay.',
  'setup.nextTitle': 'Before your next release',
  'setup.notChecked': 'Run the test, then check here',
  'setup.openIssue': 'Open the test issue',
  'setup.package': 'Package identifier · follow the platform guide',
  'setup.platform': 'App platform',
  'setup.platformGuide': 'Open the complete platform guide',
  'setup.progressHint': 'Delivery and readable source are separate checks. Creating a key alone confirms neither.',
  'setup.progressTitle': 'What we have verified',
  'setup.receiptStatus': 'This test event was received',
  'setup.received': 'Your test event arrived',
  'setup.release': 'App release',
  'setup.sourceHint': 'The event arrived, but we have not confirmed a readable source location. Open the issue and check the symbols for this release.',
  'setup.sourceStatus': 'A readable source location is available',
  'setup.subtitle': 'Install the SDK, connect this project, then verify a test event from your own app. Keep this page open while you work.',
  'setup.symbolGuide': 'Symbol upload guide',
  'setup.terminal': 'Terminal',
  'setup.testAction': 'Temporary test action',
  'setup.testHint': 'Run this once after initialisation, for example from a temporary test button. It reports a handled error without crashing the app. Remove the test afterwards.',
  'setup.title': 'Your first error, with the context to fix it.',
  'setup.verifyTitle': 'Send a test from your app',
  'setup.waiting': 'No matching test event yet',
  'setup.waitingHint': 'Allow a few seconds for delivery, then try again. Check the instance URL, ingest key, startup code and network access. Existing traffic from this project does not count as this test.',
  'shell.navigation': 'Main navigation',
  'shell.openNavigation': 'Open navigation',
  'shell.skip': 'Skip to content',
  'site.discover': 'New to Sentori? Explore the product',
  'site.language': 'Language',
  'triage.back': 'Back to issues',
  'triage.hideTimeline': 'Hide the event timeline',
  'triage.showTimeline': 'Show the event timeline',
  'auth.backToSignIn': 'Back to sign in',
  'auth.confirm': 'Confirm password',
  'auth.missingToken': 'Token missing. Open this page from the link in the email.',
  'auth.newPassword12': 'New password (at least 12 characters)',
  'auth.resetDone': 'Password updated — sign in with your new password',
  'auth.email': 'Email',
  'auth.forgot': 'Forgot password',
  'auth.forgotHint': 'Enter your email and we will send a reset link.',
  'auth.password': 'Password',
  'auth.passwordTooShort': 'Password must be at least 8 characters',
  'auth.passwordsDiffer': 'Passwords do not match',
  'auth.resetFailed': 'Reset failed — the link may have expired',
  'auth.resetPassword': 'Reset password',
  'auth.resetSent': 'If that address exists, a reset link is on its way',
  'auth.saving': 'Saving…',
  'auth.sendReset': 'Send reset link',
  'auth.sending': 'Sending…',
  'auth.setNewPassword': 'Set a new password',
  'auth.signIn': 'Sign in',
  'auth.signInFailed': 'Sign-in failed. Check the address and password.',
  'auth.signInTitle': 'Sign in to Sentori',
  'auth.signingIn': 'Signing in…',

  'common.delete': 'Delete',
  'common.retry': 'Retry',
  'inbox.awaitingFirstHint': 'This project has never received one. Point the SDK at this instance with an ingest token, and issues appear here.',
  'inbox.emptyNoProject': 'Create a project, generate an ingest token, point the SDK at this instance.',
  'inbox.group.ignored': 'Ignored',
  'inbox.group.open': 'Open',
  'inbox.group.resolved': 'Resolved',
  'inbox.envFilter': 'Environment',
  'inbox.releaseFilter': 'Release',
  'inbox.groupNew': 'New · 24h',
  'inbox.groupRegressed': 'Regressed',
  'inbox.loadFailed': 'Could not load the inbox.',
  'inbox.selectedCount': '{n} selected',

  'health.backend': 'backend',
  'health.replay': 'replay',
  'health.silent': 'no events yet',

  'identity.copied': 'Copied',
  'identity.copyHint': 'Copy the full user key',

  'instruments.assertRan': 'ran {total} · failed {failed}',
  'instruments.asserts': 'Assertions',
  'instruments.assertsEmpty':
    'No production assertions yet — sentori.assert(name, ok) creates the liveness ledger.',
  'instruments.guardedIssue': 'guarded issue',
  'instruments.colSamples': 'Samples',
  'instruments.colPrewarmed': 'Pre-warmed',
  'instruments.launch': 'Launch',
  'instruments.launchEmpty':
    'No staged launch data yet — call sentori.launch.complete() when the app becomes usable (optionally sentori.launch.mark(name) for waypoints); every start aggregates into app.launch.',
  'instruments.prewarmedTip': 'pre-warmed phantom samples, excluded from percentiles',
  'instruments.loadFailed': 'Could not load instruments.',
  'instruments.noProject': 'No project yet',
  'instruments.probeFired': 'fired {count}× · last {last}',
  'instruments.probeSilent': 'Silent since {since}. Fix holding.',
  'instruments.probes': 'Probes',
  'instruments.probesEmpty':
    'No tripwires yet — plant sentori.probe(ref) in a fixed branch and register with `sentori-cli probes sync`.',
  'instruments.colLastFail': 'Last fail',
  'instruments.colLastSeen': 'Last seen',
  'instruments.colName': 'Name',
  'instruments.colRelease': 'Release',
  'instruments.colStatus': 'Status',
  'instruments.volume': '{events} events · {users} users',
  'instruments.volumeAnon': '{events} · users unknown',
  'instruments.colVolume': 'Volume',
  'instruments.traces': 'Trace points',
  'instruments.tracesEmpty': 'No trace points. sentori.trace(name) marks a path as reached.',

  'issue.code': "Stack trace",
  'issue.codeNone': 'This event carried no stack. A native crash brings its own; a reported error only has one if the original exception object was passed to sentori.error(e) rather than a string.',
  'issue.eventMoment': '{kind} fired',
  'issue.notifyAffected': 'Notify them',
  'issue.signals': 'User events',
  'issue.signalsNone': 'No behaviour signals came with this event.',
  'issue.replayScreensEmpty':
    'Pixel replay is on for this build but captured nothing — the native binary is older than the JS SDK, or the capture is failing. Rebuild the native app.',
  'issue.replayScreensEmptyTip':
    'This build asked for pixel replay and got none — the wireframe comes from the always-on structural capture.',
  'issue.replayWireframeTip':
    'This build has replayScreens off — the wireframe comes from the always-on structural capture.',
  'issue.guardAnchored':
    'Resolved in {release}. Only a recurrence in that release or newer reopens this.',
  'issue.guardProbeHint':
    'Plant sentori.probe(ref) in the fixed branch — a silent probe is proof the fix holds.',
  'issue.guardUnanchored': 'Resolved without a release anchor. Any recurrence reopens it.',
  'issue.ignore': 'Ignore',
  'issue.deviceClock': 'device clock',
  'issue.loadFailed': 'Could not load this issue.',
  'issue.occurrences': 'Occurrences',
  'issue.releaseRegressedHere': 'regressed in this release',
  'issue.releaseResolvedHere': 'marked fixed in this release',
  'issue.releasesTitle': 'Release spread',
  'issue.reopen': 'Reopen',
  'issue.replayFrom': 'Replay from an occurrence at {when}. The latest event carries none.',
  'issue.replayNone':
    'No visual replay came with this event — set replayScreens: true in the SDK to capture the minute before.',
  'issue.resolve': 'Resolve',
  'issue.resolveInRelease': 'Fixed in release',
  'issue.timeline': 'Timeline',
  'nav.inbox': 'Inbox',
  'nav.instruments': 'Instruments',
  'nav.projects': 'Projects',
  'nav.push': 'Push',
  'nav.releases': 'Releases',
  'nav.settings': 'Settings',

  'notify.saveFailed': 'Could not save notification preferences',
  'notify.loadFailed': 'Could not load notification preferences.',
  'notify.onNewIssue': 'new issue',
  'notify.onRegression': 'regression',
  'notify.prefsHint': 'Per-project control of your own mail. All on by default.',
  'notify.prefsTitle': 'Email me about',
  'notify.webhookTitle': 'Webhook',
  'notify.webhookHint':
    'Also POST new issues and regressions here, as {"subject", "body", "metadata"}. Per project, not per person: it goes to a room. Works with no SMTP at all, which is the case it exists for.',
  'notify.webhookPlaceholder': 'https://hooks.example.com/services/…',
  'notify.webhookSave': 'Save',
  'notify.webhookSaved': 'Saved',
  'notify.webhookFailed': 'Could not save the webhook URL.',
  'notify.webhookOwnerOnly': 'Only the owner can see or change this.',
  'notify.smtpTitle': 'SMTP',
  'notify.smtpUnconfigured': 'SMTP not configured. Set SENTORI_SMTP_HOST to enable mail.',
  'notify.testButton': 'Send test email',
  'notify.testFailed': 'Test mail failed. Check the SMTP settings and the server log.',
  'notify.testSending': 'Sending…',
  'notify.deliveryFailing': 'Notifications are not being delivered — {count} failed in the last 7 days',
  'notify.lastDelivered': 'Last delivered',
  'notify.testSent': 'Test mail sent.',
  'palette.placeholder': 'Jump to a page or search issues…',


  'push.section.audience': 'Audience',
  'push.audienceTitle': 'Who this is for',
  'push.audienceEditJson': 'Edit as JSON',
  'push.audienceEditRows': 'Back to conditions',
  'push.audienceBadJson': 'Not valid JSON',
  'push.joinAll': 'Match every condition',
  'push.joinAny': 'Match any condition',
  'push.sourceIssue': 'Hit this issue',
  'push.sourceTrait': 'Person',
  'push.sourceDevice': 'Device',
  'push.sourceUser': 'User id',
  'push.opIs': 'is',
  'push.opIsNot': 'is not',
  'push.opIn': 'is one of',
  'push.opPrefix': 'starts with',
  'push.opExists': 'is set',
  'push.opVersionGte': 'version ≥',
  'push.opVersionLte': 'version ≤',
  'push.opGte': '≥',
  'push.opLte': '≤',
  'push.deviceFacts': 'Device',
  'push.audienceMessage': 'What to send',
  'push.confirmSend': 'Send to {n} devices? This cannot be recalled.',
  'push.audienceQueued': 'Queued for {n} devices',
  'push.section.credentials': 'Credentials',
  'push.section.delivery': 'Delivery',
  'push.section.devices': 'Devices',
  'push.statusAny': 'any status',
  'push.testBody': 'Body',
  'push.testSend': 'Send test',
  'push.testSubject': 'Title',
  'push.testTitle': 'Send a test',
  'push.credentialsAdd': 'Add a credential',
  'push.field.keyId': 'Key ID',
  'push.field.production': 'Environment',
  'push.field.secretKey': 'Private key',
  'push.field.subject': 'Subject',
  'push.field.teamId': 'Team ID',
  'push.field.topic': 'Bundle ID',
  'push.field.vapidPublicKey': 'VAPID public key',
  'push.hint.apns.keyId': 'The ten characters after AuthKey_ in the .p8 filename.',
  'push.hint.apns.production': 'Sandbox is for builds signed with a development profile.',
  'push.hint.apns.secretKey': 'Paste the whole .p8, line breaks included.',
  'push.hint.apns.teamId': 'Apple developer portal, top right.',
  'push.hint.apns.topic': 'Your app bundle id. Sent to Apple as apns-topic.',
  'push.hint.fcm.secretKey': 'Firebase ▸ Project settings ▸ Service accounts ▸ Generate new private key.',
  'push.hint.webpush.secretKey': 'The VAPID private key, in PEM form.',
  'push.hint.webpush.subject': 'A mailto: or https: address the push service can reach you at.',
  'push.hint.webpush.vapidPublicKey': 'base64url. Pairs with the private key below.',
  'push.option.production': 'production',
  'push.option.sandbox': 'sandbox',
  'push.placeholder.apns.keyId': 'ABC1234567',
  'push.placeholder.apns.secretKey': '-----BEGIN PRIVATE KEY-----\n…\n-----END PRIVATE KEY-----',
  'push.placeholder.apns.teamId': 'DEF7654321',
  'push.placeholder.apns.topic': 'com.example.app',
  'push.placeholder.fcm.secretKey': '{\n  "type": "service_account",\n  "project_id": …\n}',
  'push.placeholder.webpush.secretKey': '-----BEGIN PRIVATE KEY-----\n…\n-----END PRIVATE KEY-----',
  'push.placeholder.webpush.subject': 'mailto:alerts@example.com',
  'push.placeholder.webpush.vapidPublicKey': 'BEl62iUYgUiv…',
  'push.credentialsEmpty':
    'No provider credentials yet — paste an APNs key or an FCM service account above. Devices can register without one; nothing can be delivered until it is here.',
  'push.credentialsTitle': 'Provider credentials',
  'push.device': 'Device',
  'push.devicesEmpty': 'No devices. Register with sentori.push.register().',
  'push.devicesTitle': 'Devices',
  'push.failed24h': "Stopped in 24h",
  'push.specHowTo': 'Where to get it',
  'push.specApnsTitle': 'iOS · APNs',
  'push.specFcmTitle': 'Android · FCM',
  'push.specWebpushTitle': 'Web · VAPID',
  'push.specApnsStep1': 'Apple Developer → Certificates, Identifiers & Profiles → Keys',
  'push.specApnsStep2': 'New key, tick Apple Push Notifications service (APNs)',
  'push.specApnsStep3': 'Download. Once — Apple does not offer it again',
  'push.specFcmStep1': 'Firebase console → Project settings → Service accounts',
  'push.specFcmStep2': 'Generate new private key',
  'push.specFcmStep3': 'Confirm the service account holds the Firebase Cloud Messaging API Admin role',
  'push.specWebpushStep1': 'Generate a P-256 keypair yourself',
  'push.specWebpushStep2': 'Public half into the app, private half here',
  'push.specFile': 'File',
  'push.specFirstLine': 'First line',
  'push.specSize': 'Size',
  'push.specMarker': 'Marker',
  'push.secretIssue.unrecognised': 'Neither PEM nor JSON.',
  'push.credentialLabel': 'Label',
  'projects.emptyTitle': 'No projects yet',
  'projects.emptyHint': 'A project is what events, releases and push devices belong to. Name one above to start.',
  'settings.createProjectFailed': 'Could not create the project.',
  'push.section.integrate': 'Integrate',
  'push.integrateEndpoint': 'Endpoint',
  'push.integrateScopeNote': 'ingest cannot send',
  'push.integrateMint': 'Generate',
  'push.integrateSend': 'Send',
  'push.integrateCount': 'Estimate',
  'push.integrateCountWhy': 'The same query, without queueing.',
  'push.integratePoll': 'Result',
  'push.integratePollWhy': 'The send returns a sendId. sent = the vendor accepted it. delivered = the device acknowledged it.',
  'push.retry': 'retry',
  'push.readinessTitle': 'Setup',
  'push.level.blocked': 'Blocked',
  'push.level.warn': 'Gap',
  'push.level.info': 'Note',
  'push.check.no-device': 'No registered devices',
  'push.fix.no-device': 'Call sentori.push.register(). {quarantined} retired.',
  'push.check.no-credential': '{provider} · {devices} devices · no credential',
  'push.fix.no-credential': 'Nothing can be delivered until one is saved.',
  'push.check.queue-stalled': '{queued} queued · oldest {minutes}m',
  'push.fix.queue-stalled': 'Worker drains every 5s. Check push is enabled server-side.',
  'push.check.no-identity': '{live} devices · no identity',
  'push.fix.no-identity': 'Call sentori.user(). Person and issue targeting match 0.',
  'push.check.no-traits': '{live} devices · no traits',
  'push.fix.no-traits': 'sentori.user({ id, traits }). Trait conditions match 0.',
  'push.check.no-metadata': '{live} devices · no metadata',
  'push.fix.no-metadata': 'push.register({ metadata }). Version conditions match 0.',
  'push.check.credential-unused': '{provider} credential · no devices',
  'push.fix.credential-unused': 'If devices were expected, the SDK registers as something else.',
  'push.check.mass-quarantine': '{quarantined} retired · {live} live',
  'push.fix.mass-quarantine': 'Usually a credential aimed at the wrong app or Apple host.',
  'push.check.all-failing': '{failed} sends failed in 24h · none sent',
  'push.fix.all-failing': 'Vendor: {reason}',
  'push.check.apns-mixed-env': '{sandbox} of {live} on Apple sandbox',
  'push.fix.apns-mixed-env': 'Delivered per device env. One key covers both.',
  'crashFree.scope': 'Sessions in the selected App and environment over 24 hours; issue, release and tag filters do not apply',
  'crashFree.title': 'Crash-free sessions',
  'crashFree.thin': 'too few sessions to read this precisely',
  'crashFree.counts': '{crashed} crashed of {total} sessions',
  "crashFree.empty": "No sessions in this period",
  'crashFree.users': '{pct}% of users crash-free',
  'crashFree.usersEmpty': 'nobody identified yet',
  'crashFree.usersTitle':
    'Users who started at least one session in the last {hours}h. A person who ran two releases counts once.',
  'crashFree.sessionsTitle':
    'Sessions started in the last {hours}h. One app launch is one session, so a user who launched the app ten times counts ten times.',
  'health.usersTitle':
    'Distinct users who sent any event in the last 24h — not only the ones who hit a problem.',
  'crashFree.trendFrom': '{hours}h ago',
  'crashFree.trendLow': 'low {pct}%',
  'crashFree.trendTitle':
    'Crash-free sessions per bucket over the last {hours}h, drawn from {low}% up. A break in the line is a stretch with no sessions, not a healthy one.',
  'crashFree.window': 'last {hours}h',
  'platform.unknown': 'Unknown (SDK newer than this server)',
  'platform.weapp': 'WeChat Mini Program',
  'releases.legend': 'Symbolication',
  'releases.legendBroken': "Contains invalid files",
  'releases.crashFree': 'crash-free',
  'releases.crashFreeNone': 'no sessions',
  'releases.crashFreeTitle':
    '{pct}% of the {sessions} sessions on this release did not crash, over the last 30 days. Sessions arrive from SDKs that send them; a release with none shows a dash rather than a rate.',
  'releases.delete': 'Delete release',
  'releases.deleteConfirm': 'Delete {name}? Its symbolication artifacts go with it, and this release\u2019s stacks cannot be symbolicated again until they are re-uploaded.',
  'releases.deleteFailed': 'Could not delete the release.',
  'releases.emptyTitle': 'No releases yet',
  'releases.loadFailed': 'Could not load releases.',
  'replay.empty': 'No frames in this replay.',
  'replay.frameAlt': 'Replay frame at {t}s',
  'replay.loadFailed': 'Could not load the replay.',
  'replay.pause': 'Pause',
  'replay.play': 'Play',
  'replay.scrubber': 'Replay position',
  'replay.title': 'Session replay',
  'releases.noArtifacts': 'No symbolication artifacts. Stack traces stay minified:',

  'settings.adminEmail': 'admin email',
  'settings.auditLoadFailed': 'Could not load the audit log.',
  'settings.changePassword': 'Change password',
  'settings.createAdmin': 'Create admin',
  'settings.createProject': 'Create',
  'settings.currentPassword': 'current password',
  'settings.deleteAdminConfirm': 'Delete admin {email}? Their sessions end immediately.',
  'settings.deleteProjectConfirm':
    'Delete project {name}? All its events and issues go with it.',
  'settings.initialPassword': 'initial password (8+ chars)',
  'settings.language': 'Language',
  'settings.mintToken': 'Generate',
  'settings.newPassword': 'new password (12+ characters)',
  'settings.projectName': 'project name',
  'settings.revoke': 'Revoke',
  'settings.revokeConfirm':
    'Revoke token {name}? Clients using it stop reporting immediately.',
  'settings.revoked': 'revoked',
  'settings.save': 'Save',
  'settings.saved': 'Saved',
  'settings.tab.account': 'Account',
  'settings.roleOwnerScope': 'every project, plus admins, tokens and the audit log',
  'settings.roleAdminScope': 'only the projects ticked below',
  'settings.tab.audit': 'Audit',
  'settings.tab.notifications': 'Notifications',
  'settings.tab.push': 'Push',
  'settings.tab.tokens': 'Tokens',
  'settings.tab.users': 'Admins',
  'settings.colAction': 'Action',
  'settings.colActor': 'Actor',
  'settings.colCreated': 'Created',
  'settings.colLastLogin': 'Last login',
  'settings.colRole': 'Role',
  'settings.colTarget': 'Target',
  'settings.colToken': 'Token',
  'settings.colWhen': 'When',
  'settings.fieldProject': 'Project',
  'settings.fieldScope': 'Scope',
  'settings.tokenName': 'token name',
  'settings.tokenOnce': 'Shown once. Copy it now.',
  'settings.usersLoadFailed': 'Could not load admins.',
  'strip.inspectHint': "Hover or focus a marker to inspect it",
  'stack.sourceCount': "Source linked for {n} of {total} frames",
  'releaseView.identifier': 'Release identifier',
  'stack.debugFiles': "Check release debug files",
  'stack.nativeSourceMissing': "Source locations require debug files matching this build’s binary UUID. A source bundle supplies the code preview.",
  'stack.sourceMissing': "No source preview is available for this frame. Check the source map or source bundle for this release.",
  'stack.sourceReady': "Source",
  'stack.sourceUnavailable': "No source preview",
  'stack.locationUnresolved': "Unresolved location",
  'stack.locationMissing': "This frame has no resolved source file or line. Check the debug files for this release.",
  'stack.nativeLocationMissing': "This frame has no resolved source location. Check debug files matching this build’s binary UUID.",
  'stack.binaryUuid': "Binary UUID",
  'stack.address': "Address",
  'stack.libraryFrames': '{n} library frames',
  'stack.libraryFramesOpen': 'library frames',
  'stack.minified': 'minified',
  'stack.truncated': '… {n} more frames',

  'table.empty': 'Nothing here yet',

  'shell.bootFailed': 'Could not load your session.',
  'shell.projectsFailed': 'Could not load projects.',
  'shell.loading': 'Loading…',

  'strip.events': 'events',
  'strip.frames': 'frames',
  'strip.signals': 'actions',
  'theme.dark': 'Dark',
  'theme.label': 'Theme',
  'theme.light': 'Light',
  'theme.system': 'Match system',
  'shell.roleAdmin': 'Admin',
  'shell.roleOwner': 'Owner',
  "transfer.title": "Project migration",
  "transfer.description": "Move from self-hosted Sentori to Cloud or another instance, then verify live data before completing the move",
  "transfer.prepare": "Connect the two instances",
  "transfer.prepareHint": "Open this page as the owner on both instances. Download the target identity here, then load it on the source to start",
  "transfer.saveIdentity": "Save instance identity",
  "transfer.receive": "Receive a migration",
  "transfer.start": "Start a migration",
  "transfer.scope": "What moves with your projects",
  "transfer.scopeHint": "Project history, events, sessions, replay files, releases and tasks move in encrypted packages. Tokens, passwords, member access, Push credentials and automation settings must be configured again on the target",
  "transfer.limit": "Each upload is limited to 1 GiB. Move smaller groups of projects if needed. A single project larger than this limit needs an operator-assisted migration; this page cannot import it. Cloud project and storage limits also apply",
  "transfer.empty": "No migrations yet",
  "transfer.id": "Migration ID",
  "transfer.source": "Source instance",
  "transfer.target": "Target instance",
  "transfer.step.copy": "Copy history",
  "transfer.step.cutover": "Final copy and switch",
  "transfer.step.evidence": "Verify live data",
  "transfer.sourceHint": "Export the initial copy while the source stays available. Import it on the target before sealing the source and exporting the final copy. Keep the source running for older clients",
  "transfer.targetHint": "Import the initial copy, then the final package from the sealed source. Activate only after the final import succeeds. Historical data cannot prove that your SDK has switched",
  "transfer.saveDescriptor": "Save migration descriptor",
  "transfer.sdkHint": "After the final import, create new ingestion tokens on the target and update the SDK endpoint and project configuration. Reconfigure Push and automations separately",
  "transfer.evidenceHint": "This list shows the first new SDK event received by each project after activation. Wait for all projects to receive an event, then verify them",
  "transfer.waitingEvent": "Waiting for a new SDK event",
  "transfer.copyPackage": "Initial copy",
  "transfer.finalPackage": "Final copy",
  "transfer.forwarded": "Successful forwarding requests",
  "transfer.failed": "Failed forwarding requests",
  "transfer.expires": "Forwarding expires",
  "transfer.lastSeen": "Last forwarding request",
  "transfer.noTraffic": "No forwarding requests yet",
  "transfer.rollback": "Rollback and data preservation plan",
  "transfer.invalidDescriptor": "Load a valid file for this target and select at least one project when starting from the source",
  "transfer.sourceCreateHint": "Load the identity file downloaded from the target instance, then select the projects to move",
  "transfer.targetCreateHint": "Load the migration descriptor downloaded from the source. It must name this instance as the target",
  "transfer.identityFile": "Target identity file",
  "transfer.descriptorFile": "Migration descriptor file",
  "transfer.identityContent": "Target identity contents",
  "transfer.descriptorContent": "Migration descriptor contents",
  "transfer.incomplete": "Complete the required fields before continuing",
  "transfer.packageHint": "Keep the package and its recovery key together in secure storage. Each export can use a new key; import with the key saved for that exact package",
  "transfer.sealHint": "Sealing waits for accepted writes, then stops new writes to the selected source projects. Export the final copy next. Old SDK requests retry until forwarding is configured. Sealing cannot be undone from this page",
  "transfer.activateHint": "Activation opens the imported projects for new writes. If you later roll back, preserve and reconcile all new target data before reopening the source",
  "transfer.rollbackHint": "Record the responsible operator, backup location, how to preserve new target data, and how to reconcile it before returning traffic to the source (30–8,000 bytes)",
  "transfer.cutoverAck": "I have a data preservation plan and understand that switching back requires reconciling new target data",
  "transfer.sdkAck": "I have updated the SDK endpoint, project and tokens. Keep the source running while old clients still need forwarding",
  "transfer.cancelAck": "Cancel this migration before cutover. Imported staging projects on this target will be removed",
  "transfer.tailHint": "Use a new ingestion token created on the target after the final import. The source verifies the target identity before forwarding old clients. Only telemetry is forwarded; configure Push separately. Forwarding lasts at most 90 days and can be renewed here",
  "transfer.targetOrigin": "Target HTTPS origin",
  "transfer.targetToken": "New target ingestion token",
  "transfer.days": "Forwarding days (1–90)",
  "transfer.action.export_copy": "Export initial copy",
  "transfer.action.seal": "Seal source writes",
  "transfer.action.export_final": "Export final copy",
  "transfer.action.upload_copy": "Import initial copy",
  "transfer.action.upload_final": "Import final copy",
  "transfer.action.activate": "Activate target",
  "transfer.action.verify": "Verify received events",
  "transfer.action.complete": "Complete migration",
  "transfer.action.cancel": "Cancel migration",
  "transfer.action.tail": "Forward old clients",
  "transfer.state.copying": "Awaiting initial copy",
  "transfer.state.copied": "Initial copy ready",
  "transfer.state.sealed": "Source sealed",
  "transfer.state.ready": "Final copy ready",
  "transfer.state.active": "Target active",
  "transfer.state.observing": "Observing live traffic",
  "transfer.state.completed": "Migration verified",
  "transfer.state.cancelled": "Cancelled",
  "transfer.state.attention": "Needs attention",

  "transfer.ownerOnly": "Only the instance owner can manage project migrations",

  "filters.appType": "App type",
  "filters.platform": "Platform",
  "filters.source": "Tag source",
  "filters.context": "Context",
  "filters.tags": "Event tags",
  "filters.key": "Tag key",
  "filters.value": "Tag value",
  "filters.add": "Add condition",
  "filters.active": "Active tag filters",
  "filters.remove": "Remove {key} filter",
  "filters.emptyValue": "(empty)",
  "filters.sameEvent": "All conditions must match the same event. Business dimensions such as appType are available under tags.",
  "filters.invalid": "Invalid filter link. Clear filters to continue.",

  "charts.dimension": "Observation",
  "charts.plot": "Chart",
  "charts.table": "Data table",
  "charts.export": "Export CSV",
  "charts.failed": "Could not render chart",
  "charts.empty": "No observations yet",
  "charts.emptyHint": "Reported values will appear here when available",
  "workbench.overview": "Overview",
  'workbench.filteredTasks': 'Across all matching tasks',
  "workbench.activeTasks": "Active tasks",
  "workbench.reviewTasks": "Review & verification",
  "workbench.doneTasks": "Completed",
  "workbench.overdueTasks": "Overdue",
  "workbench.tasksEmptyHint": "Create a task or open an issue to track its fix, review and verification",
  "workbench.firstTask": "Create a task",
  "workbench.monitoredServices": "Monitored services",
  "workbench.projectScope": "Current project",
  "workbench.openIncidents": "Open incidents",
  "workbench.searchServices": "Search services",
  "workbench.monitorsEmptyHint": "Add a service endpoint to track availability, response times and incident recovery",
  "workbench.firstMonitor": "Add your first monitor",
  "workbench.monitorLatencyHint": "Recent successful checks; gaps indicate failed requests. Check results are listed below.",
  "workbench.instrumentsDescription": "Telemetry, launch performance and the signals behind your releases",
  "workbench.searchInstruments": "Search names or releases",
  "workbench.window": "Observation window",
  "workbench.lastHours": "Last {hours} hours",
  "workbench.launchChartHint": "Launch percentiles by release in the last {hours} hours; pre-warmed starts excluded",
  "workbench.eventActivity": "Event activity",
  "workbench.receivedWindow": "Received in the last {hours} hours",
  "workbench.topTraces": "Up to 12 most active trace points in the selected window",
  "workbench.errorEvents": "Error events",
  "workbench.warningEvents": "Warning events",
  "workbench.cumulativeHint": "Lifetime counters for the selected names and releases, up to 200 rows. Charts show the first 12. The time window applies to event activity, traces and startup.",
  "workbench.passed": "Passed",
  "workbench.failed": "Failed",
  "workbench.probeFires": "Probe fires",
  "workbench.registered": "Registered",
  "workbench.traceWindow": "Received in the last {hours} hours, up to 200 trace points",

} as const;

/** The shape every locale must satisfy in full. */
export type Messages = Record<keyof typeof en, string>;
export type MessageKey = keyof typeof en;
